import {
  API_FUNCTIONS,
  assertStatus,
  checkIfGreatSuccess,
  queryClient,
  queryKeys,
  RAW_ENDPOINTS,
  RULES_ENDPOINTS,
  sdk,
} from 'utils/query';
import { RuleItem, RuleTransition, RuleTransitions } from 'models/rules';
import { useTranslation } from 'react-i18next';
import { ApiTaskResult } from 'models/general';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { useCustomMutation } from '../useCustomMutation';

interface ExcludesRuleProps {
  item: RuleItem;
  transition: RuleTransition;
  patientId?: string;
}

const excludesRule = async ({ item, transition, patientId }: ExcludesRuleProps) => {
  if (!patientId) return new EmptyParametersError();

  const data = { patientIds: [patientId] };

  if (transition === RuleTransitions.enable) {
    const { status } = await sdk.raw.delete(
      `${RAW_ENDPOINTS.TASKS}/api/${API_FUNCTIONS.monitoring}/${RULES_ENDPOINTS.monitoringRules}/${item.id}/excludes`,
      { data },
    );
    return assertStatus({ status });
  }

  const result = await sdk.tasks.api.post<ApiTaskResult, unknown>(
    API_FUNCTIONS.monitoring,
    `${RULES_ENDPOINTS.monitoringRules}/${item.id}/excludes`,
    data,
    {},
  );
  return checkIfGreatSuccess({ result });
};

export const useExcludesRule = () => {
  const { t } = useTranslation();
  return useCustomMutation({
    fn: excludesRule,
    onSuccess: {
      message: t('success_data_updated', { value: t('alerts_rule_label') }),
      callback: async (_result, variables) => {
        const {
          item: { groupId },
          patientId,
        } = variables;

        const queryKey = patientId
          ? [`${queryKeys.patientRules}-${patientId}-${groupId}`]
          : [`${queryKeys.groupRules}-${groupId}`];

        await queryClient.invalidateQueries({ queryKey });
      },
    },
  });
};
